import React from 'react';
import { Check } from 'lucide-react';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { SUPPORTED_CURRENCIES, CURRENCIES, type CurrencyCode } from '../../types/currency.types';

interface CurrencySwitcherProps {
  /**
   * `menu`   — vertical list, fits inside a dropdown menu (header user menu).
   * `inline` — compact pill row, suitable for mobile sheets / settings rows.
   */
  variant?: 'menu' | 'inline';
  /** Optional callback after the user picks a currency (e.g. close menu). */
  onChange?: (code: CurrencyCode) => void;
}

/**
 * Lets the buyer override the auto-detected display currency. Reads the
 * current selection from the redux currency slice and writes the override
 * through `setCurrency` (which persists to localStorage).
 */
const CurrencySwitcher: React.FC<CurrencySwitcherProps> = ({ variant = 'menu', onChange }) => {
  const { currency, setCurrency } = useCurrencyFormat();

  const handlePick = (code: CurrencyCode) => {
    if (code !== currency) setCurrency(code);
    onChange?.(code);
  };

  if (variant === 'inline') {
    return (
      <div className="flex items-center gap-1.5">
        {SUPPORTED_CURRENCIES.map((code) => {
          const active = code === currency;
          return (
            <button
              key={code}
              type="button"
              onClick={() => handlePick(code)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors ${
                active
                  ? 'bg-gray-900 text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-400'
              }`}
              aria-pressed={active}
            >
              {CURRENCIES[code].symbol} {code}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div role="group" aria-label="Display currency">
      <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wide text-gray-400 font-bold">
        Currency
      </p>
      {SUPPORTED_CURRENCIES.map((code) => {
        const meta = CURRENCIES[code];
        const active = code === currency;
        return (
          <button
            key={code}
            type="button"
            onClick={() => handlePick(code)}
            className="w-full flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            <span className="inline-flex items-center gap-2">
              <span className="w-5 text-center font-bold">{meta.symbol}</span>
              <span>{meta.name}</span>
              <span className="text-[10px] text-gray-400 font-semibold">{code}</span>
            </span>
            {active && <Check size={14} className="text-emerald-600" />}
          </button>
        );
      })}
    </div>
  );
};

export default CurrencySwitcher;
